import { supabase } from '../lib/supabase';
import { formatJobRevNumber } from '../utils/projectFormatting';

export class HoldEmailService {
  /**
   * Get email tracking record for a project
   * @param {number} projectId - The project ID
   * @returns {Promise<object|null>} Email tracking record or null if not exists
   */
  static async getTracking(projectId) {
    try {
      const { data, error } = await supabase
        .from('on_hold_email_tracking')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error fetching email tracking:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getTracking:', error);
      throw error;
    }
  }

  /**
   * Create a new email tracking record
   * @param {object} trackingData - The tracking data to create
   * @returns {Promise<object>} Created tracking record
   */
  static async createTracking(trackingData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const dataToInsert = {
        ...trackingData,
        created_by: user?.email || 'unknown',
        updated_by: user?.email || 'unknown'
      };

      const { data, error } = await supabase
        .from('on_hold_email_tracking')
        .insert([dataToInsert])
        .select()
        .single();

      if (error) {
        console.error('Error creating email tracking:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in createTracking:', error);
      throw error;
    }
  }

  /**
   * Update an existing email tracking record
   * @param {number} id - The tracking record ID
   * @param {object} updates - The fields to update
   * @returns {Promise<object>} Updated tracking record
   */
  static async updateTracking(id, updates) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const dataToUpdate = {
        ...updates,
        updated_by: user?.email || 'unknown'
      };

      const { data, error } = await supabase
        .from('on_hold_email_tracking')
        .update(dataToUpdate)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating email tracking:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in updateTracking:', error);
      throw error;
    }
  }

  /**
   * Create or update email tracking for a project
   * @param {number} projectId - The project ID
   * @param {object} trackingData - The tracking data
   * @returns {Promise<object>} Created or updated tracking record
   */
  static async upsertTracking(projectId, trackingData) {
    try {
      // First check if tracking exists
      const existing = await this.getTracking(projectId);
      
      if (existing) {
        // Update existing record
        return await this.updateTracking(existing.id, trackingData);
      } else {
        // Create new record
        return await this.createTracking({
          project_id: projectId,
          ...trackingData
        });
      }
    } catch (error) {
      console.error('Error in upsertTracking:', error);
      throw error;
    }
  }

  /**
   * Generate email preview based on project and hold information
   * @param {object} project - The project object
   * @param {object} trackingData - Current tracking data
   * @param {object} contacts - Array of contacts for personalization
   * @param {object} currentUser - Current user for signature
   * @param {array} discrepancies - Array of project discrepancies to include
   * @returns {object} Email preview with subject and body
   */
  static generateEmailPreview(project, trackingData = {}, contacts = [], currentUser = null, discrepancies = null) {
    const holdReasonMap = {
      'waiting_discrepancy_clarification': 'Waiting for Discrepancy Clarification',
      'client_requested_hold': 'Client Requested Hold',
      'waiting_for_information': 'Waiting for Information',
      'internal_review': 'Internal Review',
      'waiting_updated_pdfs': 'Waiting Updated PDFs',
      'waiting_updated_cad': 'Waiting Updated CAD',
      'waiting_client_response': 'Waiting Client Response',
      'other': 'Other Reason'
    };

    // If hold reason is in map, use it. Otherwise format the hold reason nicely
    const holdReasonDisplay = holdReasonMap[project.holdReason] || 
      (project.holdReason ? 
        project.holdReason
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase())
          .replace(/Pdfs/g, 'PDFs')
          .replace(/Cad/g, 'CAD')
        : 'Project On Hold');
    const projectName = project.projectName || 'Project';
    const jobNumber = project.jobNumber || '';
    const jobRevDisplay = formatJobRevNumber(jobNumber, project.rev, '');
    const revision = jobRevDisplay.replace(jobNumber, '').trim() ? ` ${jobRevDisplay.replace(jobNumber, '').trim()}` : '';
    
    // Get contact name for personalization
    let contactFirstName = 'there';
    if (project.contactId && contacts.length > 0) {
      const primaryContact = contacts.find(c => c.id === project.contactId);
      if (primaryContact?.name) {
        const contactName = primaryContact.name;
        contactFirstName = contactName.split(' ')[0] || contactName;
      }
    }
    
    // Generate subject
    const subject = `${projectName}${revision} - ${holdReasonDisplay}`;
    
    // Generate email body with personalization
    let body = `${contactFirstName},\n\n`;
    
    // Get recurring days for follow-up
    const recurringDays = trackingData.recurring_follow_up_days || 7;
    
    // Generate grammatically correct hold message based on reason
    const holdReasonPhrases = {
      'waiting_discrepancy_clarification': 'while we await clarification on discrepancies',
      'client_requested_hold': 'as requested by the client',
      'waiting_for_information': 'while we await additional information',
      'internal_review': 'for internal review',
      'waiting_updated_pdfs': 'while we await updated PDF files',
      'waiting_updated_cad': 'while we await updated CAD files',
      'waiting_client_response': 'while we await a response from the client',
      'other': 'for review'
    };
    
    // Get the appropriate phrase for the hold reason
    const holdPhrase = holdReasonPhrases[project.holdReason] || `due to ${holdReasonDisplay.toLowerCase()}`;
    
    // Simple message about the project being on hold
    if (trackingData.last_email_sent_date) {
      body += `This project is currently on hold ${holdPhrase}. We will follow up in ${recurringDays} days.\n\n`;
    } else {
      body += `This project is being placed on hold ${holdPhrase}. We will follow up in ${recurringDays} days.\n\n`;
    }
    
    
    // Add discrepancies section if provided - format like HoldProjectModal
    if (discrepancies && discrepancies.length > 0) {
      body += `\n\nRelated Discrepancies:\n`;
      
      discrepancies.forEach(disc => {
        const discName = disc.type || disc.discrepancyType || 'Discrepancy';
        body += `${discName}:\n`;
        
        // Add additional information
        if (disc.additional_information || disc.additionalInformation) {
          body += `${disc.additional_information || disc.additionalInformation}\n`;
        }
        
        // Add file attachment info in the format "File link: URL"
        if (disc.markup_attachment || disc.markupAttachment) {
          const attachment = disc.markup_attachment || disc.markupAttachment;
          
          // Handle both string and object/array formats
          let attachmentData = attachment;
          if (typeof attachment === 'string') {
            try {
              attachmentData = JSON.parse(attachment);
            } catch (e) {
              // If parsing fails, treat as single URL
              attachmentData = attachment;
            }
          }
          
          // Handle array of attachments
          if (Array.isArray(attachmentData)) {
            attachmentData.forEach(file => {
              if (file.url) {
                body += `File link: ${file.url}\n`;
              }
            });
          } else if (attachmentData && typeof attachmentData === 'object') {
            // Handle single attachment object
            if (attachmentData.url) {
              body += `File link: ${attachmentData.url}\n`;
            }
          } else if (typeof attachmentData === 'string' && attachmentData.startsWith('http')) {
            // Handle direct URL string
            body += `File link: ${attachmentData}\n`;
          }
        }
        
        // Add Loom video link in the format "Video Explanation: URL"
        if (disc.loom_link || disc.loomLink) {
          const loomLink = disc.loom_link || disc.loomLink;
          body += `Video Explanation: ${loomLink}\n`;
        }
        
        body += '\n';
      });
    }
    
    // Simple closing
    body += `Thank you.`;
    
    return {
      subject,
      body,
      html: body.replace(/\n/g, '<br>')
    };
  }

  /**
   * Generate HTML-formatted email using the email formatter utility
   * @param {string} plainText - Plain text email content
   * @param {object} project - Project object for context
   * @param {object} currentUser - Current user for signature
   * @param {object} contacts - Contacts array
   * @param {array} discrepancies - Array of project discrepancies to include
   * @returns {Promise<string>} HTML formatted email
   */
  static async generateHTMLEmail(plainText, project, currentUser = null, contacts = [], discrepancies = null) {
    try {
      // Parse the plain text to extract components
      const lines = plainText.split('\n');
      
      // Get contact name (first line)
      let contactName = 'there';
      if (lines[0] && lines[0].includes(',')) {
        contactName = lines[0].replace(',', '').trim();
      }
      
      // Find all content between greeting and signature for the yellow box
      let contentStart = 1; // After greeting line
      let contentEnd = lines.length;
      
      // Find where the signature/closing starts
      for (let i = lines.length - 1; i >= 1; i--) {
        const line = lines[i].toLowerCase();
        if (line.includes('thank you') || 
            line.includes('best regards') ||
            line.includes('sincerely')) {
          contentEnd = i;
          break;
        }
      }
      
      // Get all content lines between greeting and signature
      const contentLines = [];
      let inEmptyLineStreak = false;
      
      for (let i = contentStart; i < contentEnd; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();
        
        // Skip multiple consecutive empty lines
        if (!trimmedLine) {
          if (!inEmptyLineStreak && contentLines.length > 0) {
            contentLines.push(''); // Add single empty line for spacing
            inEmptyLineStreak = true;
          }
          continue;
        }
        
        inEmptyLineStreak = false;
        contentLines.push(trimmedLine);
      }
      
      // Check if we have discrepancies section
      let hasDiscrepancies = false;
      let discrepancyStartIndex = -1;
      for (let i = 0; i < contentLines.length; i++) {
        if (contentLines[i].includes('Related Discrepancies:')) {
          hasDiscrepancies = true;
          discrepancyStartIndex = i;
          break;
        }
      }
      
      // Build the main content (up to discrepancies section if it exists)
      const mainContentEnd = hasDiscrepancies ? discrepancyStartIndex : contentLines.length;
      let notificationContent = contentLines
        .slice(0, mainContentEnd)
        .filter(line => line) // Remove empty lines for cleaner look
        .join(' '); // Join as single paragraph
      
      // If no content found, show placeholder
      if (!notificationContent) {
        notificationContent = 'Email content will appear here...';
      }
      
      // Build HTML for discrepancies list if they exist (yellow box style)
      let discrepanciesHtml = '';
      if (discrepancies && discrepancies.length > 0) {
        // Build the discrepancy HTML content with proper formatting
        let discrepancyContent = '<h4 style="margin: 0 0 10px 0; color: #92400e; font-size: 14px; font-weight: 600;">Related Discrepancies:</h4>';
        
        discrepancies.forEach(disc => {
          const discName = disc.type || disc.discrepancyType || 'Discrepancy';
          discrepancyContent += `<p style="margin: 10px 0 5px 0; font-weight: 600; color: #92400e; font-size: 14px;">${discName}:</p>`;
          
          // Add additional information
          if (disc.additional_information || disc.additionalInformation) {
            discrepancyContent += `<p style="margin: 0 0 5px 0; color: #78350f; font-size: 14px;">${disc.additional_information || disc.additionalInformation}</p>`;
          }
          
          // Add file attachment links
          if (disc.markup_attachment || disc.markupAttachment) {
            const attachment = disc.markup_attachment || disc.markupAttachment;
            
            // Handle both string and object/array formats
            let attachmentData = attachment;
            if (typeof attachment === 'string') {
              try {
                attachmentData = JSON.parse(attachment);
              } catch (e) {
                attachmentData = attachment;
              }
            }
            
            // Handle array of attachments
            if (Array.isArray(attachmentData)) {
              attachmentData.forEach(file => {
                if (file.url) {
                  const filename = file.filename || file.url.split('/').pop() || 'View File';
                  discrepancyContent += `<p style="margin: 0 0 5px 0; color: #78350f; font-size: 14px;"><span style="font-weight: 500;">File link:</span> <a href="${file.url}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${filename}</a></p>`;
                }
              });
            } else if (attachmentData && typeof attachmentData === 'object') {
              // Handle single attachment object
              if (attachmentData.url) {
                const filename = attachmentData.filename || attachmentData.url.split('/').pop() || 'View File';
                discrepancyContent += `<p style="margin: 0 0 5px 0; color: #78350f; font-size: 14px;"><span style="font-weight: 500;">File link:</span> <a href="${attachmentData.url}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${filename}</a></p>`;
              }
            } else if (typeof attachmentData === 'string' && attachmentData.startsWith('http')) {
              // Handle direct URL string
              const filename = attachmentData.split('/').pop() || 'View File';
              discrepancyContent += `<p style="margin: 0 0 5px 0; color: #78350f; font-size: 14px;"><span style="font-weight: 500;">File link:</span> <a href="${attachmentData}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${filename}</a></p>`;
            }
          }
          
          // Add Loom video link
          if (disc.loom_link || disc.loomLink) {
            const loomLink = disc.loom_link || disc.loomLink;
            discrepancyContent += `<p style="margin: 0 0 5px 0; color: #78350f; font-size: 14px;"><span style="font-weight: 500;">Video Explanation:</span> <a href="${loomLink}" target="_blank" rel="noopener noreferrer" style="color: #0066cc; text-decoration: underline;">${loomLink}</a></p>`;
          }
        });
        
        discrepanciesHtml = `
        <div style="background-color: #fffbeb; border: 1px solid #fde68a; padding: 15px; border-radius: 4px; margin: 20px 0;">
          ${discrepancyContent}
        </div>`;
      }
      
      // Generate the HTML using simplified template design matching Put Project on Hold modal
      const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Project Hold Notification</title>
</head>
<body style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #ffffff;">
    <div style="max-width: 600px; margin: 0 auto;">
        <p style="font-size: 14px; color: #333; margin-bottom: 20px;">${contactName},</p>
        
        <div style="background-color: #fee2e2; border: 1px solid #fecaca; padding: 15px; border-radius: 4px; margin: 20px 0;">
            <p style="margin: 0; color: #991b1b; font-size: 14px;">${notificationContent}</p>
        </div>
        
        ${discrepanciesHtml}
        
        <p style="font-size: 14px; color: #333; margin-top: 20px;">Thank you.</p>

        <div style="margin: 20px 0 16px 0; padding: 12px 16px; border-top: 1px solid #0891b2; border-bottom: 1px solid #0891b2; background-color: #f0fdfa;">
            <p style="margin: 0 0 4px 0; font-size: 13px; font-weight: 600; color: #0e7490;">Client Portal Now Available</p>
            <p style="margin: 0; font-size: 12px; color: #155e75; line-height: 1.5;">Access your projects, files, and updates at <a href="https://portal.ab-civil.com" style="color: #0891b2; text-decoration: underline; font-weight: 500;">portal.ab-civil.com</a>. Log in with your email—most users already have access. Anyone can manage their team's access from within the portal.</p>
        </div>
    </div>
</body>
</html>`;
      
      return html;
    } catch (error) {
      console.error('Error generating HTML email:', error);
      // Fallback to simple HTML conversion
      return plainText.replace(/\n/g, '<br>');
    }
  }

  /**
   * Get email templates from discrepancy_and_clarification_type table
   * @returns {Promise<array>} Array of email templates
   */
  static async getEmailTemplates() {
    try {
      const { data, error } = await supabase
        .from('discrepancy_and_clarification_type')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching email templates:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEmailTemplates:', error);
      throw error;
    }
  }

  /**
   * Mark tracking as resolved when project is taken off hold
   * @param {number} projectId - Project ID
   * @param {string} reason - Resolution reason
   * @returns {Promise<object>} Updated tracking record
   */
  static async markAsResolved(projectId, reason = 'Project status changed') {
    try {
      const tracking = await this.getTracking(projectId);
      if (!tracking || tracking.hold_status === 'resolved') {
        return null; // No tracking or already resolved
      }

      const updates = {
        hold_status: 'resolved',
        resolved_date: new Date().toISOString(),
        resolution_notes: reason
      };

      return await this.updateTracking(tracking.id, updates);
    } catch (error) {
      console.error('Error in markAsResolved:', error);
      // Don't throw - this is optional tracking
      return null;
    }
  }

  /**
   * Mark email as sent and update tracking
   * @param {number} trackingId - The tracking record ID
   * @returns {Promise<object>} Updated tracking record
   */
  static async markEmailSent(trackingId) {
    try {
      const now = new Date().toISOString();
      
      // Get current record to update reminder_sent_dates array
      const { data: current, error: fetchError } = await supabase
        .from('on_hold_email_tracking')
        .select('reminder_sent_dates')
        .eq('id', trackingId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const reminderDates = current.reminder_sent_dates || [];
      reminderDates.push(now);

      const updates = {
        last_email_sent_date: now,
        reminder_sent_dates: reminderDates,
        email_status: 'sent'
      };

      return await this.updateTracking(trackingId, updates);
    } catch (error) {
      console.error('Error in markEmailSent:', error);
      throw error;
    }
  }

  /**
   * Mark email as sent for a project (using project_id)
   * @param {number} projectId - The project ID
   * @returns {Promise<object>} Updated tracking record
   */
  static async markEmailSentByProjectId(projectId) {
    try {
      const now = new Date().toISOString();
      
      // Get current record to update reminder_sent_dates array
      const { data: current, error: fetchError } = await supabase
        .from('on_hold_email_tracking')
        .select('id, reminder_sent_dates')
        .eq('project_id', projectId)
        .single();

      if (fetchError) {
        console.error('Error fetching tracking for project:', projectId, fetchError);
        // If no tracking exists yet, that's okay - email was sent directly
        return null;
      }

      const reminderDates = current.reminder_sent_dates || [];
      reminderDates.push(now);

      const updates = {
        last_email_sent_date: now,
        reminder_sent_dates: reminderDates,
        email_status: 'sent'
      };

      return await this.updateTracking(current.id, updates);
    } catch (error) {
      console.error('Error in markEmailSentByProjectId:', error);
      // Don't throw - this is optional tracking
      return null;
    }
  }

  /**
   * Validate email payload sizes to prevent "payload string too long" errors
   * @param {string} emailPreview - Plain text email content
   * @param {string} emailHtml - HTML email content
   * @param {string} emailSubject - Email subject line
   * @throws {Error} If payload is too large
   */
  static validateEmailPayload(emailPreview, emailHtml, emailSubject) {
    const maxTextSize = 64000; // ~64KB limit for text fields
    const maxHtmlSize = 512000; // ~512KB limit for HTML content
    
    if (emailPreview && emailPreview.length > maxTextSize) {
      throw new Error(`Email content is too large (${emailPreview.length.toLocaleString()} characters). Maximum allowed is ${maxTextSize.toLocaleString()} characters. Please reduce the content length.`);
    }
    
    if (emailHtml && emailHtml.length > maxHtmlSize) {
      throw new Error(`Email HTML is too large (${emailHtml.length.toLocaleString()} characters). Maximum allowed is ${maxHtmlSize.toLocaleString()} characters. Please reduce the HTML content.`);
    }
    
    if (emailSubject && emailSubject.length > 255) {
      throw new Error(`Email subject is too long (${emailSubject.length} characters). Maximum allowed is 255 characters.`);
    }
  }

  /**
   * Send hold email immediately (similar to DiscrepancyService.sendDiscrepancyEmail)
   * @param {number} projectId - The project ID
   * @param {string} emailPreview - Plain text email content
   * @param {string} emailHtml - HTML formatted email content
   * @param {string} emailSubject - Email subject line
   * @param {Array} emailTo - Array of contact IDs for To field
   * @param {Array} emailCc - Array of contact IDs for CC field
   * @param {object} additionalData - Additional tracking data (initial_follow_up_days, recurring_follow_up_days, etc.)
   * @returns {Promise<object>} Updated tracking record
   */
  static async sendHoldEmail(projectId, emailPreview, emailHtml, emailSubject, emailTo = [], emailCc = [], additionalData = {}) {
    try {
      // Validate payload sizes
      this.validateEmailPayload(emailPreview, emailHtml, emailSubject);

      const { data: { user } } = await supabase.auth.getUser();
      const now = new Date().toISOString();

      // Check if tracking exists for this project
      const existing = await this.getTracking(projectId);
      
      if (existing) {
        // First, set send_email to false to reset the trigger
        await supabase
          .from('on_hold_email_tracking')
          .update({ 
            send_email: false
          })
          .eq('id', existing.id);

        // Update existing tracking with email data and trigger send
        const reminderDates = existing.reminder_sent_dates || [];
        reminderDates.push(now);

        const updates = {
          email_preview: emailPreview,
          email_html: emailHtml,
          email_subject: emailSubject,
          email_to: emailTo,
          email_cc: emailCc,
          send_email: true, // This triggers the n8n workflow
          last_email_sent_date: now,
          reminder_sent_dates: reminderDates,
          email_status: additionalData.is_one_time_email ? 'draft' : 'sent',
          auto_recurring_email: additionalData.auto_recurring_email || false,
          initial_follow_up_days: additionalData.initial_follow_up_days || 7,
          recurring_follow_up_days: additionalData.recurring_follow_up_days || 7,
          updated_by: user?.email || 'unknown',
          // Set quick_deadline_email_sent_at if this is a one-time email
          ...(additionalData.is_one_time_email && { quick_deadline_email_sent_at: now })
        };

        const { data, error } = await supabase
          .from('on_hold_email_tracking')
          .update(updates)
          .eq('id', existing.id)
          .select()
          .single();

        if (error) {
          throw new Error(`Failed to send email: ${error.message}`);
        }
        return data;
      } else {
        // Create new tracking record with email data
        const trackingData = {
          project_id: projectId,
          email_preview: emailPreview,
          email_html: emailHtml,
          email_subject: emailSubject,
          email_to: emailTo,
          email_cc: emailCc,
          send_email: false, // Start as false, will set to true after creation
          last_email_sent_date: now,
          reminder_sent_dates: [now],
          email_status: additionalData.is_one_time_email ? 'draft' : 'sent',
          auto_recurring_email: additionalData.auto_recurring_email || false,
          initial_follow_up_days: additionalData.initial_follow_up_days || 7,
          recurring_follow_up_days: additionalData.recurring_follow_up_days || 7,
          created_by: user?.email || 'unknown',
          updated_by: user?.email || 'unknown',
          // Set quick_deadline_email_sent_at if this is a one-time email
          ...(additionalData.is_one_time_email && { quick_deadline_email_sent_at: now })
        };

        const { data: created, error: createError } = await supabase
          .from('on_hold_email_tracking')
          .insert([trackingData])
          .select()
          .single();

        if (createError) {
          throw new Error(`Failed to create email tracking: ${createError.message}`);
        }

        // Now trigger the email send by setting send_email to true
        const { data, error } = await supabase
          .from('on_hold_email_tracking')
          .update({ 
            send_email: true
          })
          .eq('id', created.id)
          .select()
          .single();

        if (error) {
          throw new Error(`Failed to trigger email send: ${error.message}`);
        }
        return data;
      }
    } catch (error) {
      console.error('Error sending hold email:', error);
      throw error;
    }
  }

  /**
   * Get all projects with pending email reminders
   * @returns {Promise<array>} Array of projects needing email reminders
   */
  static async getPendingReminders() {
    try {
      const { data, error } = await supabase
        .from('v_active_hold_emails')
        .select('*')
        .in('email_action_status', ['due', 'pending_first_send']);

      if (error) {
        console.error('Error fetching pending reminders:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error in getPendingReminders:', error);
      throw error;
    }
  }
}